import type { ComponentType } from "react";
import { Button } from "@/components/ui";
import { cn } from "@/lib/cn";

interface CompactActionInfo {
  label: string;
  shortcut?: string[];
  description?: string[];
}

function CompactActionButton({
  icon: Icon,
  label,
  shortcut,
  tooltipDescription,
  testId,
  onInfoChange,
  onClick,
  active,
  ariaExpanded,
  ariaControls,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  shortcut?: string[];
  tooltipDescription?: string[];
  testId: string;
  onInfoChange: (info: CompactActionInfo | null) => void;
  onClick: () => void;
  active?: boolean;
  ariaExpanded?: boolean;
  ariaControls?: string;
}) {
  const info = { label, shortcut, description: tooltipDescription };
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn(
        "grid h-8 w-8 place-items-center rounded-full transition-[background-color,color,box-shadow] hover:bg-white/20 hover:text-primary aria-[pressed=true]:bg-white/20 aria-[pressed=true]:text-primary",
        active
          ? "bg-white/20 text-primary"
          : "text-muted",
      )}
      data-testid={testId}
      data-active={active ? "true" : undefined}
      aria-label={label}
      aria-pressed={active === undefined ? undefined : active}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      onBlur={() => onInfoChange(null)}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onInfoChange(null);
        onClick();
      }}
      onFocus={() => onInfoChange(info)}
      onPointerEnter={() => onInfoChange(info)}
      onPointerLeave={() => onInfoChange(null)}
    >
      <Icon className="h-4 w-4" />
    </Button>
  );
}

function CompactActionInfoPanel({ info }: { info: CompactActionInfo }) {
  return (
    <div
      className="flex h-16 w-full items-center p-2"
      data-testid="desktop-compact-action-info"
    >
      <span className="flex h-12 min-w-0 flex-1 flex-col justify-center gap-0.5 rounded-[16px] px-3 py-1.5 text-left">
        <span className="block whitespace-normal text-canvas-md font-medium leading-tight text-primary">
          {info.label}
        </span>
        {info.description?.slice(0, 1).map((line) => (
          <span
            key={line}
            className="block whitespace-normal text-canvas-sm leading-tight text-muted"
          >
            {line}
          </span>
        ))}
      </span>
    </div>
  );
}

export { CompactActionButton, CompactActionInfoPanel };
export type { CompactActionInfo };
